import React, { useState, useEffect } from 'react'
import './AppConfig.scss'
import { AppConfigXModel, UpdateAppConfigPayload } from 'BoostXpressAdmin/Model/appConfigXModel'
import { appConfigBXService } from 'BoostXpressAdmin/Services/appConfigBXService'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import { HorizontalLoader } from 'Components'

const AppConfig: React.FC = () => {
  const [configs, setConfigs] = useState<AppConfigXModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editingPlatform, setEditingPlatform] = useState<'ANDROID' | 'IOS' | null>(null)

  const [formData, setFormData] = useState<UpdateAppConfigPayload>({
    platform: 'ANDROID',
    minSupportedVersion: '',
    latestVersion: '',
    isForceUpdateEnabled: false,
    updateTitle: '',
    updateMessage: '',
    storeUrl: '',
  })

  const loadConfigs = async () => {
    try {
      setIsLoading(true)
      const res = await appConfigBXService.getAppConfigs()
      setConfigs(res.data.data ?? [])
    } catch (e) {
      setConfigs([])
      handleError(e as { response?: { data?: { error: string } } })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadConfigs()
  }, [])

  const handleEdit = (config: AppConfigXModel | null, platform: 'ANDROID' | 'IOS') => {
    setEditingPlatform(platform)
    if (config) {
      setFormData({
        platform: config.platform,
        minSupportedVersion: config.minSupportedVersion,
        latestVersion: config.latestVersion,
        isForceUpdateEnabled: config.isForceUpdateEnabled,
        updateTitle: config.updateTitle,
        updateMessage: config.updateMessage,
        storeUrl: config.storeUrl,
      })
    } else {
      setFormData({
        platform,
        minSupportedVersion: '',
        latestVersion: '',
        isForceUpdateEnabled: false,
        updateTitle: '',
        updateMessage: '',
        storeUrl: '',
      })
    }
  }

  const handleCancel = () => {
    setEditingPlatform(null)
    setFormData({
      platform: 'ANDROID',
      minSupportedVersion: '',
      latestVersion: '',
      isForceUpdateEnabled: false,
      updateTitle: '',
      updateMessage: '',
      storeUrl: '',
    })
  }

  const handleSave = async () => {
    if (!formData.minSupportedVersion || !formData.latestVersion || !formData.updateTitle || !formData.updateMessage || !formData.storeUrl) {
      handleError({ response: { data: { error: 'Please fill in all required fields' } } })
      return
    }

    setIsSaving(true)
    try {
      await appConfigBXService.updateAppConfig(formData)
      handleSuccess('App config updated successfully!')
      handleCancel()
      loadConfigs()
    } catch (e) {
      handleError(e as { response?: { data?: { error: string } } })
    } finally {
      setIsSaving(false)
    }
  }

  const getConfigByPlatform = (platform: 'ANDROID' | 'IOS') => {
    return configs.find((c) => c.platform === platform)
  }

  const androidConfig = getConfigByPlatform('ANDROID')
  const iosConfig = getConfigByPlatform('IOS')

  return (
    <div className='app-config-container w-100'>
      <div className='d-flex align-items-center justify-content-between w-100'>
        <p className='app-config-title mb-0'>App Configuration Management</p>
      </div>

      <div className='row w-100 mt-3 m-0'>
        {/* Android Config */}
        <div className='col-md-6'>
          <div className='config-card w-100 mb-4'>
            <div className='config-card-header'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center gap-2'>
                  <div className='platform-icon platform-icon--android'>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M17.523 15.3414C17.523 16.7264 16.398 17.8514 15.013 17.8514C13.628 17.8514 12.503 16.7264 12.503 15.3414C12.503 13.9564 13.628 12.8314 15.013 12.8314C16.398 12.8314 17.523 13.9564 17.523 15.3414Z'
                        fill='#3DDC84'
                      />
                      <path
                        d='M6.47699 15.3414C6.47699 16.7264 5.35199 17.8514 3.96699 17.8514C2.58199 17.8514 1.45699 16.7264 1.45699 15.3414C1.45699 13.9564 2.58199 12.8314 3.96699 12.8314C5.35199 12.8314 6.47699 13.9564 6.47699 15.3414Z'
                        fill='#3DDC84'
                      />
                      <path
                        d='M18.707 8.8214C18.393 8.0514 17.896 7.3814 17.268 6.8714L17.972 5.5114C18.072 5.3214 18.003 5.0814 17.813 4.9814C17.623 4.8814 17.383 4.9514 17.283 5.1414L16.569 6.5214C15.823 6.1214 14.949 5.9014 14.003 5.9014C13.057 5.9014 12.183 6.1214 11.437 6.5214L10.723 5.1414C10.623 4.9514 10.383 4.8814 10.193 4.9814C10.003 5.0814 9.933 5.3214 10.033 5.5114L10.737 6.8714C10.109 7.3814 9.612 8.0514 9.298 8.8214H18.707ZM12.003 8.1314C11.453 8.1314 11.003 7.6814 11.003 7.1314C11.003 6.5814 11.453 6.1314 12.003 6.1314C12.553 6.1314 13.003 6.5814 13.003 7.1314C13.003 7.6814 12.553 8.1314 12.003 8.1314ZM16.003 8.1314C15.453 8.1314 15.003 7.6814 15.003 7.1314C15.003 6.5814 15.453 6.1314 16.003 6.1314C16.553 6.1314 17.003 6.5814 17.003 7.1314C17.003 7.6814 16.553 8.1314 16.003 8.1314Z'
                        fill='#3DDC84'
                      />
                    </svg>
                  </div>
                  <h3 className='config-card-title mb-0'>Android</h3>
                </div>
                {editingPlatform !== 'ANDROID' && (
                  <button 
                    className='btn btn-default config-edit-btn-' 
                    onClick={() => handleEdit(androidConfig || null, 'ANDROID')}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className='config-card-body'>
              {isLoading ? (
                <div className='config-loading'>
                  <HorizontalLoader />
                </div>
              ) : editingPlatform === 'ANDROID' ? (
                <div className='config-form'>
                  <div className='form-group'>
                    <label className='form-label'>Min Supported Version</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.minSupportedVersion}
                      onChange={(e) => setFormData({ ...formData, minSupportedVersion: e.target.value })}
                      placeholder='e.g. 1.0.0'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Latest Version</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.latestVersion}
                      onChange={(e) => setFormData({ ...formData, latestVersion: e.target.value })}
                      placeholder='e.g. 2.0.0'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Force Update</label>
                    <div className='form-check'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={formData.isForceUpdateEnabled}
                        onChange={(e) => setFormData({ ...formData, isForceUpdateEnabled: e.target.checked })}
                      />
                      <label className='form-check-label'>Enable force update</label>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Update Title</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.updateTitle}
                      onChange={(e) => setFormData({ ...formData, updateTitle: e.target.value })}
                      placeholder='e.g. New Update Available'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Update Message</label>
                    <textarea
                      className='form-control'
                      rows={3}
                      value={formData.updateMessage}
                      onChange={(e) => setFormData({ ...formData, updateMessage: e.target.value })}
                      placeholder='Enter update message...'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Store URL</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.storeUrl}
                      onChange={(e) => setFormData({ ...formData, storeUrl: e.target.value })}
                      placeholder='https://play.google.com/store/apps/details?id=com.boostxpress'
                    />
                  </div>

                  <div className='config-form-actions'>
                    <button className='btn btn-secondary' onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </button>
                    <button className='btn btn-default' onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : androidConfig ? (
                <div className='config-display'>
                  <div className='config-item'>
                    <span className='config-label'>Min Supported Version:</span>
                    <span className='config-value'>{androidConfig.minSupportedVersion}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Latest Version:</span>
                    <span className='config-value'>{androidConfig.latestVersion}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Force Update:</span>
                    <span className={`config-value ${androidConfig.isForceUpdateEnabled ? 'config-value--enabled' : 'config-value--disabled'}`}>
                      {androidConfig.isForceUpdateEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Update Title:</span>
                    <span className='config-value'>{androidConfig.updateTitle}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Update Message:</span>
                    <span className='config-value'>{androidConfig.updateMessage}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Store URL:</span>
                    <span className='config-value config-value--url'>{androidConfig.storeUrl}</span>
                  </div>
                  {androidConfig.updatedAt && (
                    <div className='config-item'>
                      <span className='config-label'>Last Updated:</span>
                      <span className='config-value'>{new Date(androidConfig.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {androidConfig.updatedByAdminEmail && (
                    <div className='config-item'>
                      <span className='config-label'>Updated By:</span>
                      <span className='config-value'>{androidConfig.updatedByAdminEmail}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className='config-empty'>
                  <p>No configuration found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* iOS Config */}
        <div className='col-md-6'>
          <div className='config-card w-100 mb-4'>
            <div className='config-card-header'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center gap-2'>
                  <div className='platform-icon platform-icon--ios'>
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z'
                        fill='#000000'
                      />
                    </svg>
                  </div>
                  <h3 className='config-card-title mb-0'>iOS</h3>
                </div>
                {editingPlatform !== 'IOS' && (
                  <button 
                    className='btn btn-default config-edit-btn-' 
                    onClick={() => handleEdit(iosConfig || null, 'IOS')}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className='config-card-body'>
              {isLoading ? (
                <div className='config-loading'>
                  <HorizontalLoader />
                </div>
              ) : editingPlatform === 'IOS' ? (
                <div className='config-form'>
                  <div className='form-group'>
                    <label className='form-label'>Min Supported Version</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.minSupportedVersion}
                      onChange={(e) => setFormData({ ...formData, minSupportedVersion: e.target.value })}
                      placeholder='e.g. 1.0.0'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Latest Version</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.latestVersion}
                      onChange={(e) => setFormData({ ...formData, latestVersion: e.target.value })}
                      placeholder='e.g. 2.0.0'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Force Update</label>
                    <div className='form-check'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={formData.isForceUpdateEnabled}
                        onChange={(e) => setFormData({ ...formData, isForceUpdateEnabled: e.target.checked })}
                      />
                      <label className='form-check-label'>Enable force update</label>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Update Title</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.updateTitle}
                      onChange={(e) => setFormData({ ...formData, updateTitle: e.target.value })}
                      placeholder='e.g. New Update Available'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Update Message</label>
                    <textarea
                      className='form-control'
                      rows={3}
                      value={formData.updateMessage}
                      onChange={(e) => setFormData({ ...formData, updateMessage: e.target.value })}
                      placeholder='Enter update message...'
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Store URL</label>
                    <input
                      type='text'
                      className='form-control'
                      value={formData.storeUrl}
                      onChange={(e) => setFormData({ ...formData, storeUrl: e.target.value })}
                      placeholder='https://apps.apple.com/app/boostxpress'
                    />
                  </div>

                  <div className='config-form-actions'>
                    <button className='btn btn-secondary' onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </button>
                    <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : iosConfig ? (
                <div className='config-display'>
                  <div className='config-item'>
                    <span className='config-label'>Min Supported Version:</span>
                    <span className='config-value'>{iosConfig.minSupportedVersion}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Latest Version:</span>
                    <span className='config-value'>{iosConfig.latestVersion}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Force Update:</span>
                    <span className={`config-value ${iosConfig.isForceUpdateEnabled ? 'config-value--enabled' : 'config-value--disabled'}`}>
                      {iosConfig.isForceUpdateEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Update Title:</span>
                    <span className='config-value'>{iosConfig.updateTitle}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Update Message:</span>
                    <span className='config-value'>{iosConfig.updateMessage}</span>
                  </div>
                  <div className='config-item'>
                    <span className='config-label'>Store URL:</span>
                    <span className='config-value config-value--url'>{iosConfig.storeUrl}</span>
                  </div>
                  {iosConfig.updatedAt && (
                    <div className='config-item'>
                      <span className='config-label'>Last Updated:</span>
                      <span className='config-value'>{new Date(iosConfig.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {iosConfig.updatedByAdminEmail && (
                    <div className='config-item'>
                      <span className='config-label'>Updated By:</span>
                      <span className='config-value'>{iosConfig.updatedByAdminEmail}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className='config-empty'>
                  <p>No configuration found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  )
}

export default AppConfig
